import React from 'react';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';

import { Input, Dropdown, Checkbox, PhoneNumber } from '../../../components/Forms';
import { Button } from '../../../components/Buttons';
import EnvironmentDropdown, { EnvironmentDropdownProps } from '../../components/EnvironmentDropdown';
import { validateValues } from '../newLocation/validation/validateValues';
import { validateInput, getPhonePrefixFromCountry } from './utils';
import { getPostalPlace } from '../../../helpers/dataload/postalCodes';
import { getTypeOptions, getCountryOptions } from './options';
import { LocationFormValues } from '../types';
import { useIsDinteroRoot } from '../../../auth/accessToken/Allow';
import _upperFirst from 'lodash/upperFirst';

import Grid from '@mui/material/Grid';

interface LocationFormProps {
    locationFormValues: LocationFormValues;
    onSubmit: (values: LocationFormValues) => void;
    accountIds: string[];
    isEdit?: boolean;
}

const LocationForm = (
    {
        locationFormValues,
        onSubmit,
        accountIds,
        isEdit,
    }: LocationFormProps
) => {
    const { t } = useTranslation('app');
    const isDinteroAdmin = useIsDinteroRoot();

    const typeOptions = getTypeOptions(t);
    const countryOptions = getCountryOptions(t, '');

    const disableUneditable = isEdit && !isDinteroAdmin;

    return (
        <Formik
            initialValues={locationFormValues}
            validate={(values) => validateValues(values, t)}
            onSubmit={(values) => onSubmit(values)}
            enableReinitialize
        >
            {({ values, handleChange, handleBlur, handleSubmit, setFieldValue, errors, touched }) => (
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={1} alignItems="center" flexDirection="row">
                        {isEdit && <>
                            <Grid item xs={12} sm={6}>
                                <EnvironmentDropdown
                                    disabled={isEdit}
                                    {...({
                                        label: t('locations.location_form.fields.environment'),
                                        placeholder: t('locations.location_form.fields.environment'),
                                        value: values.accountId,
                                        name: 'accountId',
                                        onChange: (value: string) => {
                                            setFieldValue('accountId', value);
                                        },
                                        accountIds: accountIds,
                                    } as EnvironmentDropdownProps)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Dropdown
                                    name={'country'}
                                    label={t('locations.location_form.fields.country')}
                                    value={values.country}
                                    options={countryOptions}
                                    onChange={(option) => {
                                        setFieldValue('country', option);
                                        setFieldValue('countryPrefix', getPhonePrefixFromCountry(option.value));
                                    }}
                                    disabled={disableUneditable}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Input
                                    name={'locationId'}
                                    label={t('locations.location_form.fields.location_id')}
                                    placeholder={t('locations.location_form.placeholders.location_id')}
                                    value={values.locationId}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    disabled={disableUneditable}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Input
                                    name={'organizationNumber'}
                                    label={t('locations.location_form.fields.org_nr')}
                                    placeholder={t('locations.location_form.placeholders.org_nr')}
                                    value={values.organizationNumber}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    validation={validateInput('organizationNumber', errors, touched)}
                                    disabled={disableUneditable}
                                />
                            </Grid>
                        </>}
                        <Grid item xs={12} sm={6}>
                            <Input
                                name={'businessName'}
                                label={t('locations.location_form.fields.legal_name')}
                                placeholder={t('locations.location_form.placeholders.legal_name')}
                                value={values.businessName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                validation={validateInput('businessName', errors, touched)}
                                disabled={disableUneditable}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Input
                                name={'name'}
                                label={t('locations.location_form.fields.name')}
                                placeholder={t('locations.location_form.placeholders.name')}
                                value={values.name}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                validation={validateInput('name', errors, touched)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Dropdown
                                name={'type'}
                                label={t('locations.location_form.fields.type')}
                                value={values.type}
                                options={typeOptions}
                                onChange={(value) => setFieldValue('type', value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Input
                                name={'websiteUrl'}
                                label={t('locations.location_form.fields.website')}
                                placeholder={t('locations.location_form.placeholders.website')}
                                value={values.websiteUrl}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Input
                                name={'addressLine'}
                                label={t('locations.location_form.fields.address_line')}
                                placeholder={t('locations.location_form.placeholders.address_line')}
                                value={values.addressLine}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                validation={validateInput('addressLine', errors, touched)}
                                disabled={disableUneditable}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Input
                                name={'addressLine2'}
                                label={t('locations.location_form.fields.address_line_2')}
                                placeholder={t('locations.location_form.placeholders.address_line_2')}
                                value={values.addressLine2}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={disableUneditable}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Input
                                name={'postalCode'}
                                label={t('locations.location_form.fields.postal_code')}
                                placeholder={t('locations.location_form.placeholders.postal_code')}
                                value={values.postalCode}
                                onChange={(event) => {
                                    getPostalPlace(values.country.value, event.currentTarget.value).then((place: string) => {
                                        if (place) {
                                            setFieldValue('postalPlace', _upperFirst(place.toLowerCase()));
                                        }
                                    });
                                    handleChange(event);
                                }}
                                onBlur={handleBlur}
                                validation={validateInput('postalCode', errors, touched)}
                                disabled={disableUneditable}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Input
                                name={'postalPlace'}
                                label={t('locations.location_form.fields.postal_place')}
                                placeholder={t('locations.location_form.placeholders.postal_place')}
                                value={values.postalPlace}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                validation={validateInput('postalPlace', errors, touched)}
                                disabled={disableUneditable}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Checkbox
                                name="franchise"
                                label={t('locations.location_form.fields.franchise')}
                                checked={values.franchise}
                                onChange={() => setFieldValue('franchise', !values.franchise)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Input
                                name={'franchiseName'}
                                label={t('locations.location_form.fields.franchise_name')}
                                placeholder={t('locations.location_form.placeholders.franchise_name')}
                                value={values.franchiseName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                validation={validateInput('franchiseName', errors, touched)}
                                disabled={!values.franchise}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <PhoneNumber
                                name={'phoneNumber'}
                                label={t('locations.location_form.fields.phone_number')}
                                phoneNumber={values.phoneNumber}
                                countryPrefix={values.countryPrefix}
                                countryPrefixName="countryPrefix"
                                setFieldValue={setFieldValue}
                                onBlur={handleBlur}
                                disabled={false}
                                autoComplete="off"
                                validation={validateInput('phoneNumber', errors, touched)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Input
                                name={'email'}
                                label={t('locations.location_form.fields.email')}
                                placeholder={t('locations.location_form.placeholders.email')}
                                value={values.email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Button type="submit">
                                {isEdit
                                    ? t('locations.location_edit.action_button')
                                    : t('locations.new_location.action_button')}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            )}
        </Formik>
    );
};

export default LocationForm;
